import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  doc, 
  query, 
  where,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

// ==========================================
// TOILETTEN-FUNKTIONEN
// ==========================================

export const getToiletsByCity = async (city) => {
  try {
    const q = query(
      collection(db, 'toilets'),
      where('city', '==', city)
    );
    const querySnapshot = await getDocs(q);
    const toilets = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();

      const isApproved = data.status === 'approved' || data.isPending === false;
     
      if (isApproved) {
        toilets.push({ id: doc.id, ...data });
      }
    });
    return toilets;
  } catch (error) {
    console.error('❌ Datenbank-Fehler (getToiletsByCity):', error);

    if (error.code === 'unavailable') {
      throw new Error('🌐 Keine Internetverbindung. Bitte überprüfe deine Netzwerkverbindung.');
    } else if (error.code === 'permission-denied') {
      throw new Error('🔒 Keine Berechtigung. Bitte melde dich erneut an.');
    } else {
      throw new Error(`⚠️ Toiletten für ${city} konnten nicht geladen werden. Bitte später erneut versuchen.`);
    }
  }
};

// Neue Toilette hinzufügen
export const addToilet = async (toiletData) => {
  try {
    const docRef = await addDoc(collection(db, 'toilets'), {
      ...toiletData,
      createdAt: serverTimestamp(),
      reviews: toiletData.reviews || 0,
      rating: toiletData.rating || 0,
      images: toiletData.images || [],
      comments: toiletData.comments || []
    });
    
    // Admin-Benachrichtigung senden
    await sendAdminNotification('new_toilet', {
      toiletName: toiletData.name,
      city: toiletData.city,
      suggestedBy: toiletData.suggestedBy || 'Unbekannt'
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Fehler beim Erstellen der Toilette:', error);
    throw error;
  }
};

// Toilette aktualisieren (für Bewertungen)
export const updateToilet = async (toiletId, updates) => {
  try {
    const toiletRef = doc(db, 'toilets', toiletId);
    await updateDoc(toiletRef, updates);
  } catch (error) {
    console.error('Fehler beim Aktualisieren:', error);
    throw error;
  }
};

// ==========================================
// FOTO-FUNKTIONEN
// ==========================================
export const uploadPhoto = async (file, toiletId) => {
  try {
    const compressedFile = await compressImage(file);
    
    const timestamp = Date.now();
    const fileName = `toilets/${toiletId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);
    
    await uploadBytes(storageRef, compressedFile);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Fehler beim Foto-Upload:', error);
    throw error;
  }
};

const compressImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1200;
        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          }));
        }, 'image/jpeg', 0.8);
      };
    };
  });
};

// ==========================================
// MELDUNGEN
// ==========================================

export const reportClosure = async (toiletId, reportData) => {
  try {
    await addDoc(collection(db, 'reports'), {
      toiletId,
      ...reportData,
      createdAt: serverTimestamp()
    });
    
    // Admin-Benachrichtigung
    await sendAdminNotification('closure_report', {
      toiletName: reportData.toiletName,
      reason: reportData.reason,
      city: reportData.city
    });
  } catch (error) {
    console.error('Fehler beim Melden:', error);
    throw error;
  }
};

// ==========================================
// ADMIN-BENACHRICHTIGUNGEN
// ==========================================

export const sendAdminNotification = async (type, data) => {
  try {
    await addDoc(collection(db, 'admin_notifications'), {
      type,
      data,
      read: false,
      createdAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Fehler bei Admin-Benachrichtigung:', error);
  }
};

// ==========================================
// ADMIN-FUNKTIONEN
// ==========================================

// Toilette freigeben (isPending auf false setzen)
export const approveToilet = async (toiletId, approvalData = {}) => {
  try {
    const toiletRef = doc(db, 'toilets', toiletId);

    const updateData = {
      isPending: false,
      approvedAt: serverTimestamp()
    };

    if (approvalData.approvedBy) {
      updateData.approvedBy = approvalData.approvedBy;
    } else {
      updateData.approvedBy = 'Admin'; 
    }

    if (approvalData.approvedById) {
      updateData.approvedById = approvalData.approvedById;
    }

    if (approvalData.approvedDate) {
      updateData.approvedDate = approvalData.approvedDate;
    }
    
    await updateDoc(toiletRef, updateData);

  } catch (error) {
    console.error('❌ Fehler beim Freigeben:', error);
    throw error;
  }
};

// Toilette ablehnen (aus Datenbank löschen)
export const rejectToilet = async (toiletId) => {
  try {
    const toiletRef = doc(db, 'toilets', toiletId);
    await deleteDoc(toiletRef);
  } catch (error) {
    console.error('❌ Fehler beim Ablehnen:', error);
    throw error;
  }
};

// Toilette löschen (Admin)
export const deleteToilet = async (toiletId) => {
  try {
    const toiletRef = doc(db, 'toilets', toiletId);
    await deleteDoc(toiletRef);
  } catch (error) {
    console.error('❌ Fehler beim Löschen:', error);
    throw error;
  }
};

// Bewertung löschen (Admin)
export const deleteReview = async (toiletId, reviewIndex, reviewUser) => {
  try {
    
    // Toilette laden
    const toiletRef = doc(db, 'toilets', toiletId);
    const toiletDoc = await getDocs(query(collection(db, 'toilets'), where('__name__', '==', toiletId)));
    
    if (toiletDoc.empty) {
      throw new Error('Toilette nicht gefunden');
    }
    
    const toiletData = toiletDoc.docs[0].data();
    const comments = toiletData.comments || [];
    
    // Bewertung entfernen
    const updatedComments = comments.filter((_, index) => index !== reviewIndex);
    
    // Neue Rating berechnen
    let newRating = 0;
    let newReviewCount = updatedComments.length;
    
    if (updatedComments.length > 0) {
      const totalRating = updatedComments.reduce((sum, comment) => sum + comment.rating, 0);
      newRating = Math.round((totalRating / updatedComments.length) * 10) / 10;
    }
    
// Firebase aktualisieren
    await updateDoc(toiletRef, {
      comments: updatedComments,
      rating: newRating,
      reviews: newReviewCount
    });

    // Zugehörigen Eintrag aus reviews-Collection löschen
try {
      const deletedComment = comments[reviewIndex];
      if (deletedComment) {
        const reviewsQueryAll = query(
          collection(db, 'reviews'),
          where('toiletId', '==', toiletId)
        );
      const reviewsSnap = await getDocs(reviewsQueryAll);
        for (const reviewDoc of reviewsSnap.docs) {
          const rData = reviewDoc.data();
          if (rData.user === deletedComment.user && rData.text === deletedComment.text) {
            await deleteDoc(reviewDoc.ref);
            break;
          }
        }
      }
    } catch (reviewDeleteError) {
      console.error('⚠️ reviews-Collection-Eintrag konnte nicht gelöscht werden:', reviewDeleteError);
    }
    
    return { success: true, newRating, newReviewCount };
  } catch (error) {
    console.error('❌ Fehler beim Löschen der Bewertung:', error);
    throw error;
  }
};

// Foto löschen (Admin)
export const deletePhoto = async (toiletId, photoUrl) => {
  try {
    
    // 1. Foto aus Firebase Storage löschen
    try {
      const photoRef = ref(storage, photoUrl);
      await deleteObject(photoRef);
    } catch (storageError) {
      if (storageError.code === 'storage/object-not-found') {
      } else {
        console.error('Fehler beim Löschen aus Storage:', storageError);
      }
    }
    
    // 2. Foto aus Toiletten-Daten entfernen
    const toiletRef = doc(db, 'toilets', toiletId);
    const q = query(collection(db, 'toilets'), where('__name__', '==', toiletId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Toilette nicht gefunden');
    }
    
    const toiletData = querySnapshot.docs[0].data();
    const images = toiletData.images || [];
    
    // Foto-Objekte UND String-URLs unterstützen
    const updatedImages = images.filter(img => {
      const imgUrl = typeof img === 'string' ? img : img.url;
      return imgUrl !== photoUrl;
    });

    const updatedComments = (toiletData.comments || []).map(comment => ({
      ...comment,
      photos: (comment.photos || []).filter(photo => {
        const photoUrlFromObj = typeof photo === 'string' ? photo : photo.url;
        return photoUrlFromObj !== photoUrl;
      })
    }));
    
    // Firebase aktualisieren
    await updateDoc(toiletRef, {
      images: updatedImages,
      comments: updatedComments
    });
    
    return { success: true, updatedImages, updatedComments };
  } catch (error) {
    console.error('❌ Fehler beim Löschen des Fotos:', error);
    throw error;
  }
};

// Alle Reports laden (Admin)
export const getAllReports = async () => {
  try {
    const reportsSnapshot = await getDocs(collection(db, 'reports'));
    
    const reports = [];
    reportsSnapshot.forEach((doc) => {
      reports.push({ id: doc.id, ...doc.data() });
    });
    return reports;
  } catch (error) {
    console.error('❌ Fehler beim Laden der Reports:', error);
    throw error;
  }
};

// Reports für eine bestimmte Toilette löschen (Admin)
export const deleteReportsForToilet = async (toiletId) => {
  try {
    
    const q = query(collection(db, 'reports'), where('toiletId', '==', toiletId));
    const querySnapshot = await getDocs(q);
    
    const deletePromises = [];
    querySnapshot.forEach((document) => {
      deletePromises.push(deleteDoc(doc(db, 'reports', document.id)));
    });
    
    await Promise.all(deletePromises);
    return { success: true, count: deletePromises.length };
  } catch (error) {
    console.error('❌ Fehler beim Löschen der Reports:', error);
    throw error;
  }
};

// Toilette als geschlossen markieren (Admin)
export const closeToilet = async (toiletId, reason, temporaryUntil = null) => {
  try {
    
    const toiletRef = doc(db, 'toilets', toiletId);
    const updateData = {
      isClosed: true,
      closedReason: reason,
      closedAt: serverTimestamp(),
      closedBy: 'Admin'
    };
    
    if (temporaryUntil) {
      updateData.closedUntil = temporaryUntil;
    }
    
    await updateDoc(toiletRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('❌ Fehler beim Schließen der Toilette:', error);
    throw error;
  }
};

// Toilette wieder öffnen (Admin)
export const reopenToilet = async (toiletId) => {
  try {
    
    const toiletRef = doc(db, 'toilets', toiletId);
    await updateDoc(toiletRef, {
      isClosed: false,
      closedReason: null,
      closedAt: null,
      closedBy: null,
      closedUntil: null,
      reopenedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('❌ Fehler beim Öffnen der Toilette:', error);
    throw error;
  }
};

// Diese Funktion löscht die Toilette UND alle zugehörigen Reports
export const permanentlyDeleteToilet = async (toiletId) => {
  try {
    
    // 1. Alle Reports für diese Toilette löschen
    await deleteReportsForToilet(toiletId);
    
    // 2. Die Toilette selbst löschen
    await deleteDoc(doc(db, 'toilets', toiletId));

    return { success: true };
  } catch (error) {
    console.error('❌ Fehler beim permanenten Löschen:', error);
    throw error;
  }
};

export const getAllToilets = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'toilets'));
    
    const toilets = [];
    querySnapshot.forEach((doc) => {
      toilets.push({ id: doc.id, ...doc.data() });
    });

    return toilets;
  } catch (error) {
    console.error('❌ Fehler beim Laden aller Toiletten:', error);
    throw error;
  }
};

// ==========================================
// NEUE FUNKTIONEN FÜR ÖFFNUNGSZEITEN
// ==========================================
export const extractAndCleanToiletData = async (toiletId, currentName) => {
  try {
    const toiletRef = doc(db, 'toilets', toiletId);

    const extractOpeningHours = (name) => {
      if (!name) return null;
      
      const patterns = [
        /\(([^)]*(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h|\d{1,2}:\d{2}|\d{1,2}-\d{1,2})[^)]*)\)/i,
        /\[([^\]]*(?:24\/7|Mo|Di|Mi|Do|Fr|Sa|So)[^\]]*)\]/i,
        /\{([^}]*(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h)[^}]*)\}/i,
        /(?:Öffnungszeiten?:?\s*)([^\n,.;]+(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h)[^\n,.;]*)/i
      ];
      
      for (const pattern of patterns) {
        const match = name.match(pattern);
        if (match) {
          return match[1].trim();
        }
      }
      
      return null;
    };

    const cleanNameFromOpeningHours = (name) => {
      if (!name) return name;
      
      let cleaned = name
        .replace(/\s*\([^)]*(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h|\d{1,2}:\d{2}|\d{1,2}-\d{1,2})[^)]*\)/gi, '')
        .replace(/\s*\[[^\]]*(?:24\/7|Mo|Di|Mi|Do|Fr|Sa|So)[^\]]*\]/gi, '')
        .replace(/\s*\{[^}]*(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h)[^}]*\}/gi, '')
        .replace(/\s*(?:Öffnungszeiten?:?\s*)([^\n,.;]+(?:Mo|Di|Mi|Do|Fr|Sa|So|Uhr|h)[^\n,.;]*)/gi, '');
      
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
      return cleaned;
    };
    
    const openingHoursRaw = extractOpeningHours(currentName);
    const cleanedName = cleanNameFromOpeningHours(currentName);
    
    const updateData = {
      name: cleanedName,
      updatedAt: serverTimestamp()
    };

    if (openingHoursRaw) {
      updateData.openingHours = openingHoursRaw;
    }
    
    await updateDoc(toiletRef, updateData);
    
    return {
      success: true,
      cleanedName,
      openingHours: openingHoursRaw
    };
    
  } catch (error) {
    console.error('❌ Fehler beim Bereinigen:', error);
    throw error;
  }
};

/**
 * Bereinigt alle Toiletten einer Stadt
 */
export const cleanAllToiletsInCity = async (city) => {
  try {
    const toilets = await getToiletsByCity(city);

    let cleaned = 0;
    let errors = 0;
    
    for (const toilet of toilets) {
      try {
        const result = await extractAndCleanToiletData(toilet.id, toilet.name);
        if (result.openingHours) {
          cleaned++;
        }
      } catch (error) {
        errors++;
        console.error(`❌ Fehler bei ${toilet.id}:`, error);
      }
    }
  
    return { cleaned, errors, total: toilets.length };
    
  } catch (error) {
    console.error('❌ Fehler bei Massen-Bereinigung:', error);
    throw error;
  }
};

// ==========================================
// KOMMENTAR-REPORT-SYSTEM 
// ==========================================

export const reportComment = async (toiletId, commentIndex, comment, reason, reporterName) => {
  try {
    const reportData = {
      toiletId,
      commentIndex,
      commentUser: comment.user || 'Unbekannt',
      commentText: comment.text || '',
      commentRating: comment.rating || 0,
      commentDate: comment.date || null,
      reportReason: reason,
      reportedBy: reporterName,
      reportedAt: serverTimestamp(),
      status: 'pending'
    };
    
    const docRef = await addDoc(collection(db, 'commentReports'), reportData);
    
    // Admin-Benachrichtigung senden
    await sendAdminNotification('comment_report', {
      toiletId,
      commentUser: comment.user,
      reason,
      reportedBy: reporterName
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('❌ Fehler beim Melden des Kommentars:', error);
    throw error;
  }
};

export const getAllCommentReports = async () => {
  try {
    const reportsRef = collection(db, 'commentReports');
    const q = query(
      reportsRef, 
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    
    const reports = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      reportedAt: doc.data().reportedAt?.toDate?.() || new Date()
    }));
    
    // Client-seitig sortieren (neueste zuerst)
    reports.sort((a, b) => b.reportedAt - a.reportedAt);
    
    return reports;
  } catch (error) {
    console.error('❌ Fehler beim Laden der Kommentar-Reports:', error);
    return [];
  }
};

export const resolveCommentReport = async (reportId) => {
  try {
    await deleteDoc(doc(db, 'commentReports', reportId));
    return { success: true };
  } catch (error) {
    console.error('❌ Fehler beim Löschen des Reports:', error);
    throw error;
  }
};

export const deleteReportsForComment = async (toiletId, commentIndex) => {
  try {
    const reportsRef = collection(db, 'commentReports');
    const q = query(
      reportsRef,
      where('toiletId', '==', toiletId),
      where('commentIndex', '==', commentIndex)
    );
    const snapshot = await getDocs(q);
    
    const deletePromises = snapshot.docs.map(docSnap => deleteDoc(doc(db, 'commentReports', docSnap.id)));
    await Promise.all(deletePromises);
    
    return { success: true, count: snapshot.docs.length };
  } catch (error) {
    console.error('❌ Fehler beim Löschen der Kommentar-Reports:', error);
    throw error;
  }
};

// ==========================================
// OPTIMIERTE PENDING PHOTOS LADEN
// ==========================================

export const getPendingPhotosOnly = async () => {
  
  const toiletsQuery = query(
    collection(db, 'toilets'),
    where('isPendingPhotos', '==', true)
  );
  
  const querySnapshot = await getDocs(toiletsQuery);
  
  const photosWithContext = [];
  
  querySnapshot.forEach((doc) => {
    const toilet = { id: doc.id, ...doc.data() };
    
    console.log(`📊 ${toilet.name} hat ${toilet.images?.length || 0} Bilder`);
    
    // 1. Pending Images (Haupt-Fotos)
    if (toilet.images && Array.isArray(toilet.images)) {
      toilet.images.forEach((img, imgIndex) => {
        if (typeof img === 'object' && img.status === 'pending') {
          photosWithContext.push({
            url: img.url,
            status: img.status,
            uploadedBy: img.uploadedBy || 'Unbekannt',
            uploadedAt: img.uploadedAt || null,
            toiletId: toilet.id,
            toiletName: toilet.name,
            toiletAddress: toilet.address,
            toiletCity: toilet.city,
            photoIndex: imgIndex,
            fromComment: false,
            commentIndex: null,
            commentUser: null,
            reviewId: img.reviewId || null
          });
        }
      });
    }
    
    // 2. Pending Comment Photos (Review-Fotos)
    if (toilet.comments && Array.isArray(toilet.comments)) {
      toilet.comments.forEach((comment, commentIndex) => {
        if (comment.photos && Array.isArray(comment.photos)) {
          comment.photos.forEach((photo, photoIndex) => {
            if (typeof photo === 'object' && photo.status === 'pending') {
              photosWithContext.push({
                url: photo.url,
                status: photo.status,
                uploadedBy: photo.uploadedBy || 'Unbekannt',
                uploadedAt: photo.uploadedAt || null,
                toiletId: toilet.id,
                toiletName: toilet.name,
                toiletAddress: toilet.address,
                toiletCity: toilet.city,
                photoIndex: photoIndex,
                fromComment: true,
                commentIndex: commentIndex,
                commentUser: comment.user || 'Unbekannt',
                reviewId: photo.reviewId || null
              });
            }
          });
        }
      });
    }
  });
  
  return photosWithContext;
};

// Cleanup: isPendingPhotos Flags korrigieren
export const cleanupPendingPhotoFlags = async () => {
  
  // Alle Toiletten mit Flag laden
  const toiletsQuery = query(
    collection(db, 'toilets'),
    where('isPendingPhotos', '==', true)
  );
  
  const snapshot = await getDocs(toiletsQuery);
  
  let fixed = 0;
  
  for (const docSnap of snapshot.docs) {
    const toilet = docSnap.data();
    
    // Prüfe ob tatsächlich pending Photos existieren
    const hasPendingInImages = (toilet.images || []).some(img => 
      typeof img === 'object' && img.status === 'pending'
    );
    
    const hasPendingInComments = (toilet.comments || []).some(c => 
      c.photos && c.photos.some(p => 
        typeof p === 'object' && p.status === 'pending'
      )
    );
    
    const shouldHaveFlag = hasPendingInImages || hasPendingInComments;
    
    // Wenn Flag gesetzt ist, aber keine pending Photos existieren
    if (!shouldHaveFlag) {
      await updateDoc(docSnap.ref, { isPendingPhotos: false });
      fixed++;
    }
  }

  return fixed;
};